import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  IconButton,
  Typography,
  Radio,
  FormControlLabel,
  Paper,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { QuizQuestion, CreateQuizQuestionData, UpdateQuizQuestionData } from '../../types/quiz';

interface QuestionFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateQuizQuestionData | UpdateQuizQuestionData) => Promise<void>;
  quizId: number;
  questionCount: number;
  initialData?: QuizQuestion;
  isPublished?: boolean;
  /** The quiz's configured default, applied to new questions. */
  defaultTimeLimitSeconds?: number;
}

interface QuestionOption {
  optionText: string;
  isCorrect: boolean;
  optionOrder: number;
}

// Kahoot-style colors
const OPTION_COLORS = ['#E21B3C', '#1368CE', '#D89E00', '#26890C', '#FF8C1A', '#9C4CDD'];

const QuestionFormDialog: React.FC<QuestionFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  quizId: _quizId,
  questionCount,
  initialData,
  isPublished = false,
  defaultTimeLimitSeconds = 30,
}) => {
  const [questionText, setQuestionText] = useState('');
  const [timeLimitSeconds, setTimeLimitSeconds] = useState(defaultTimeLimitSeconds);
  const [options, setOptions] = useState<QuestionOption[]>([
    { optionText: '', isCorrect: true, optionOrder: 1 },
    { optionText: '', isCorrect: false, optionOrder: 2 },
    { optionText: '', isCorrect: false, optionOrder: 3 },
    { optionText: '', isCorrect: false, optionOrder: 4 },
  ]);
  const [correctOptionIndex, setCorrectOptionIndex] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setQuestionText(initialData.questionText);
      setTimeLimitSeconds(initialData.timeLimitSeconds ?? defaultTimeLimitSeconds);
      if (initialData.options && initialData.options.length > 0) {
        const sortedOptions = [...initialData.options].sort((a, b) => a.optionOrder - b.optionOrder);
        const formattedOptions = sortedOptions.map((opt, idx) => ({
          optionText: opt.optionText,
          isCorrect: opt.isCorrect,
          optionOrder: idx + 1,
        }));
        setOptions(formattedOptions);
        const correctIdx = formattedOptions.findIndex((opt) => opt.isCorrect);
        setCorrectOptionIndex(correctIdx >= 0 ? correctIdx : 0);
      }
    } else {
      setQuestionText('');
      setTimeLimitSeconds(defaultTimeLimitSeconds);
      setOptions([
        { optionText: '', isCorrect: true, optionOrder: 1 },
        { optionText: '', isCorrect: false, optionOrder: 2 },
        { optionText: '', isCorrect: false, optionOrder: 3 },
        { optionText: '', isCorrect: false, optionOrder: 4 },
      ]);
      setCorrectOptionIndex(0);
    }
    setErrors({});
  }, [initialData, open, defaultTimeLimitSeconds]);

  const handleOptionTextChange = (index: number, text: string) => {
    const newOptions = [...options];
    newOptions[index].optionText = text;
    setOptions(newOptions);
  };

  const handleCorrectOptionChange = (index: number) => {
    const newOptions = options.map((opt, idx) => ({
      ...opt,
      isCorrect: idx === index,
    }));
    setOptions(newOptions);
    setCorrectOptionIndex(index);
  };

  const handleAddOption = () => {
    if (options.length >= 6) return;
    setOptions([
      ...options,
      { optionText: '', isCorrect: false, optionOrder: options.length + 1 },
    ]);
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) return;
    const newOptions = options.filter((_, idx) => idx !== index);
    // Reorder
    const reorderedOptions = newOptions.map((opt, idx) => ({
      ...opt,
      optionOrder: idx + 1,
    }));
    setOptions(reorderedOptions);

    // Adjust correct option index if needed
    if (index === correctOptionIndex) {
      setCorrectOptionIndex(0);
      reorderedOptions[0].isCorrect = true;
    } else if (index < correctOptionIndex) {
      setCorrectOptionIndex(correctOptionIndex - 1);
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!questionText.trim()) {
      newErrors.questionText = 'Question text is required';
    } else if (questionText.trim().length > 2000) {
      newErrors.questionText = 'Question text must be less than 2000 characters';
    }

    if (!timeLimitSeconds || timeLimitSeconds < 5) {
      newErrors.timeLimitSeconds = 'Time limit must be at least 5 seconds';
    } else if (timeLimitSeconds > 300) {
      newErrors.timeLimitSeconds = 'Time limit cannot exceed 300 seconds (5 minutes)';
    }

    let hasEmptyOption = false;
    options.forEach((opt, idx) => {
      if (!opt.optionText.trim()) {
        newErrors[`option_${idx}`] = 'Option text is required';
        hasEmptyOption = true;
      }
    });

    if (hasEmptyOption) {
      newErrors.options = 'All options must have text';
    }

    const correctCount = options.filter((opt) => opt.isCorrect).length;
    if (correctCount !== 1) {
      newErrors.correct = 'Exactly one option must be marked as correct';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      const data: CreateQuizQuestionData | UpdateQuizQuestionData = {
        questionText: questionText.trim(),
        questionOrder: initialData ? initialData.questionOrder : questionCount + 1,
        timeLimitSeconds: timeLimitSeconds,
        options: options.map((opt) => ({
          optionText: opt.optionText.trim(),
          isCorrect: opt.isCorrect,
          optionOrder: opt.optionOrder,
        })),
      };

      await onSubmit(data);
      // Only close if submission was successful
      onClose();
    } catch (error: unknown) {
      console.error('Error submitting question:', error);
      // Set backend error in state to display to user
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { message?: string; error?: string } } }).response?.data?.message ||
            (error as { response?: { data?: { message?: string; error?: string } } }).response?.data?.error ||
            'Failed to save question';
      setErrors({ ...errors, submit: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isPublished
          ? 'View Question'
          : initialData
          ? 'Edit Question'
          : `Add Question ${questionCount + 1}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Backend Error Alert */}
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          {/* Question Text */}
          <TextField
            fullWidth
            label="Question Text"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            error={!!errors.questionText}
            helperText={errors.questionText}
            required
            multiline
            rows={3}
            autoFocus={!isPublished}
            sx={{ mb: 3 }}
            InputProps={{ readOnly: isPublished }}
            disabled={isPublished}
          />

          {/* Time Limit */}
          <TextField
            fullWidth
            type="number"
            label="Time Limit (seconds)"
            value={timeLimitSeconds}
            onChange={(e) => setTimeLimitSeconds(Number(e.target.value))}
            error={!!errors.timeLimitSeconds}
            helperText={errors.timeLimitSeconds || 'How many seconds students have to answer this question (5-300)'}
            required
            inputProps={{ min: 5, max: 300, step: 5 }}
            sx={{ mb: 3 }}
            InputProps={{ readOnly: isPublished }}
            disabled={isPublished}
          />

          <Divider sx={{ my: 3 }} />

          {/* Options Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Answer Options ({options.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select the correct answer and fill in all option texts
            </Typography>
          </Box>

          {errors.options && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.options}
            </Alert>
          )}

          {errors.correct && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.correct}
            </Alert>
          )}

          {/* Options List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {options.map((option, index) => (
              <Paper
                key={index}
                sx={{
                  p: 2,
                  border: 2,
                  borderColor: option.isCorrect ? 'success.main' : 'grey.300',
                  bgcolor: option.isCorrect ? 'success.light' : 'background.paper',
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={1}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: OPTION_COLORS[index % OPTION_COLORS.length],
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {String.fromCharCode(65 + index)}
                    </Box>
                  </Grid>
                  <Grid item xs={isPublished ? 10 : 8}>
                    <TextField
                      fullWidth
                      placeholder={`Option ${index + 1}`}
                      value={option.optionText}
                      onChange={(e) => handleOptionTextChange(index, e.target.value)}
                      error={!!errors[`option_${index}`]}
                      helperText={errors[`option_${index}`]}
                      size="small"
                      InputProps={{ readOnly: isPublished }}
                      disabled={isPublished}
                    />
                  </Grid>
                  {!isPublished && (
                    <>
                      <Grid item xs={2}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={option.isCorrect}
                              onChange={() => handleCorrectOptionChange(index)}
                              color="success"
                            />
                          }
                          label="Correct"
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveOption(index)}
                          disabled={options.length <= 2}
                          title="Remove option"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </>
                  )}
                  {isPublished && option.isCorrect && (
                    <Grid item xs={1}>
                      <Chip label="Correct" color="success" size="small" />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}
          </Box>

          {/* Add Option Button */}
          {!isPublished && (
            <>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddOption}
                disabled={options.length >= 6}
                fullWidth
              >
                Add Option ({options.length}/6)
              </Button>

              {options.length >= 6 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Maximum 6 options allowed
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {isPublished ? 'Close' : 'Cancel'}
        </Button>
        {!isPublished && (
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={submitting}>
            {submitting ? 'Saving...' : initialData ? 'Update Question' : 'Add Question'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuestionFormDialog;
